/**
 * Keeps a region from scrolling past its content.
 *
 * An `overflow: auto` box on a phone rubber-bands even when the children
 * fit, which is how a short chat or room list can be dragged off the page.
 * Overflow is hidden while the content fits, and restored to auto once it
 * does not.
 */

import { useLayoutEffect } from 'react'
import type { RefObject } from 'react'

const OVERFLOW_SLOP_PX = 1

export function contentOverflows(element: HTMLElement): boolean {
  return element.scrollHeight - element.clientHeight > OVERFLOW_SLOP_PX
}

export function applyContainedScroll(element: HTMLElement): void {
  element.style.overflowY = contentOverflows(element) ? 'auto' : 'hidden'
}

export function useContainedScroll<T extends HTMLElement>(ref: RefObject<T | null>): void {
  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) return undefined

    const sync = (): void => applyContainedScroll(element)
    sync()

    if (typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined
    }

    const resize = new ResizeObserver(sync)
    const observeTree = (): void => {
      resize.disconnect()
      resize.observe(element)
      for (const child of Array.from(element.children)) resize.observe(child)
    }
    const mutations = new MutationObserver(() => {
      observeTree()
      sync()
    })
    observeTree()
    mutations.observe(element, { childList: true, subtree: true })

    return () => {
      mutations.disconnect()
      resize.disconnect()
    }
  }, [ref])
}
